import React from 'react';
import { ErrorMessage, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Row } from 'react-bootstrap';
import { SalvButton, SalvModal, SalvNumberField, SalvTextField, Size } from '@salvapp/ui-components';
import * as Yup from 'yup';
import './RateLimitGroupEditModal.scss';
import { RateLimitBucket } from './RateLimiterConfig';

const validationSchema = (allBucketNames: string[], state: RateLimiterGroupModalState) => {
  return Yup.object().shape({
    defaultGroup: Yup.boolean(),
    name: Yup.string().test({
      test: (name) => {
        if (state === RateLimiterGroupModalState.EDIT_DEFAULT_GROUP) {
          return true;
        } else if (name === undefined) {
          return new Yup.ValidationError('Group name is required', null, 'name');
        } else if (allBucketNames.includes(name)) {
          return new Yup.ValidationError('Group with this name already exists', null, 'name');
        } else {
          return true;
        }
      },
    }),
    limit: Yup.number().required('Rate limit is required').min(1, 'Rate limit must be greater than 0'),
  });
};

export enum RateLimiterGroupModalState {
  CREATE_NEW_GROUP,
  EDIT_GROUP,
  EDIT_DEFAULT_GROUP,
}

interface Props {
  bucket?: RateLimitBucket;
  setShow: (show: boolean) => void;
  onSubmit: (bucketName: string, limitPerSecond: number) => Promise<void>;
  state: RateLimiterGroupModalState;
  otherBucketNames: string[];
}

export const RateLimiterGroupEditModal = ({ bucket, setShow, onSubmit, state, otherBucketNames }: Props) => {
  const initialValues = {
    name: state === RateLimiterGroupModalState.CREATE_NEW_GROUP ? '' : bucket?.bucketName || '',
    limit: bucket?.limitPerSecond ?? 1,
  };

  type FormikValues = typeof initialValues;
  const isEditing = state !== RateLimiterGroupModalState.CREATE_NEW_GROUP;

  const submit = async (values: FormikValues, { setSubmitting }: FormikHelpers<FormikValues>) => {
    try {
      setSubmitting(true);
      const { name, limit } = values;
      onSubmit(name, limit!).finally(() => {
        setSubmitting(false);
      });
    } catch (_) {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      onSubmit={submit}
      validationSchema={validationSchema(otherBucketNames, state)}
    >
      {({
        values,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        isValid,
        touched,
        errors,
      }: FormikProps<FormikValues>) => {
        return (
          <SalvModal show={true} onHide={() => setShow(false)} className='rate-limit-modal'>
            <Form>
              <SalvModal.Header closeButton>
                <div className='title-s'>{isEditing ? 'Edit group' : 'New group'}</div>
              </SalvModal.Header>
              <SalvModal.Body>
                <Row className='m-b-32'>
                  <SalvTextField
                    className='w-100'
                    label='Group name'
                    value={values.name}
                    disabled={state === RateLimiterGroupModalState.EDIT_DEFAULT_GROUP}
                    onChange={(value: string) => setFieldValue('name', value)}
                    onBlur={() => setFieldTouched('name', true, true)}
                    isInvalid={touched.name && !!errors.name}
                  />
                  <ErrorMessage
                    name='name'
                    render={(message) => <div className={'salv-error-message'}>{message}</div>}
                  />
                </Row>
                <Row className='m-b-32'>
                  <SalvNumberField
                    label='Rate limit'
                    className='w-100'
                    minValue={1}
                    size={Size.MEDIUM}
                    value={values.limit}
                    onChange={(value: number) => setFieldValue('limit', value)}
                  />
                  <ErrorMessage
                    name='limit'
                    render={(message) => <div className={'salv-error-message'}>{message}</div>}
                  />
                </Row>
              </SalvModal.Body>
              <SalvModal.Footer>
                <SalvButton label='Close' variant='muted' onClick={() => setShow(false)} />
                <SalvButton label='Save' disabled={isSubmitting || !isValid} type='submit' variant='primary' />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default RateLimiterGroupEditModal;
